import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { corners } from './genericBlock';
import { toLayout, type PlanFrame } from './planFrame';
import readings from './prinsenhofConventsPlan.json';

export const PRINSENHOF_CONVENTS = readings;
export type PrinsenhofPlan = typeof readings.cecilia;
export type PrinsenhofPart = PrinsenhofPlan['chapel'];

export function prinsenhofPlan(landmark: Landmark): PrinsenhofPlan {
  if (landmark.id === 'landmark-sint-cecilia') return readings.cecilia;
  if (landmark.id === 'landmark-sint-catharina') return readings.catharina;
  throw new Error(`Unknown Prinsenhof convent: ${landmark.id}`);
}

export function prinsenhofFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Prinsenhof convent lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export function prinsenhofPartFrame(plan: PlanFrame, part: PrinsenhofPart): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export function prinsenhofPartRect(part: PrinsenhofPart) {
  return { u0: -part.length / 2, u1: part.length / 2, v0: -part.width / 2, v1: part.width / 2 };
}

export function prinsenhofObstacles(landmark: Landmark): Ring[] {
  const plan = prinsenhofFrame(landmark),
    reading = prinsenhofPlan(landmark);
  return [reading.chapel, ...reading.ranges].flatMap((part) => {
    const frame = prinsenhofPartFrame(plan, part),
      rect = prinsenhofPartRect(part),
      rooms = part.passage
        ? [
            { ...rect, u1: -part.passage / 2 },
            { ...rect, u0: part.passage / 2 },
          ]
        : [rect];
    return rooms.map((room) => corners(room).map((p) => toLayout(frame, p)));
  });
}

/** Ground patches belong to the two distinct compounds, not the whole modern hotel block. */
export function prinsenhofFloors(landmark: Landmark) {
  const reading = prinsenhofPlan(landmark),
    plan = prinsenhofFrame(landmark),
    beds = [],
    g = reading.garden;
  for (let row = 0; row < g.rows; row++)
    for (let col = 0; col < g.columns; col++) {
      const du = (g.u1 - g.u0) / g.columns,
        dv = (g.v1 - g.v0) / g.rows;
      beds.push({
        ring: corners({
          u0: g.u0 + col * du + 0.4,
          u1: g.u0 + (col + 1) * du - 0.4,
          v0: g.v0 + row * dv + 0.4,
          v1: g.v0 + (row + 1) * dv - 0.4,
        }).map((p) => toLayout(plan, p)),
        height: 0.035,
      });
    }
  const entry = reading.ranges.find((p) => p.id === reading.entryRange)!;
  return {
    beds,
    court: { ring: reading.court.map((p): Vec2 => toLayout(plan, [p[0]!, p[1]!])), height: 0.03 },
    entry: {
      ring: corners({
        ...prinsenhofPartRect(entry),
        u0: -entry.passage / 2,
        u1: entry.passage / 2,
      }).map((p) => toLayout(prinsenhofPartFrame(plan, entry), p)),
      part: entry,
    },
  };
}

/** The west portal meets local quay ground, then rises through the passage to the court. */
export function prinsenhofEntryHeight(
  frame: PlanFrame,
  part: PrinsenhofPart,
  p: Vec2,
  terrain: (p: Vec2) => number,
  base: number
): number {
  const dx = p[0] - frame.origin[0],
    dy = p[1] - frame.origin[1],
    v = -dx * Math.sin(frame.heading) + dy * Math.cos(frame.heading),
    t = Math.max(0, Math.min(1, (part.width / 2 - v) / part.width)),
    start = terrain(toLayout(frame, [0, part.width / 2]));
  return start + (base - start) * t + 0.03;
}
